///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { LinkFeature } from '../../../entities/link/link-feature';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { GtthLinkFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { _cloneDeep } from '../../../../../shared/utils';

export interface LinkNodesData {
    assignedName0: string;
    assignedName1: string;
    ip0: string;
    ip1: string;
}

export class GtthChangeLinkNodesDataCommand extends GtthAbstractCommand {
    private readonly modifiedLinksMap: Map<number, LinkNodesData> = new Map<number, LinkNodesData>();
    private readonly originalLinksMap: Map<number, LinkNodesData> = new Map<number, LinkNodesData>();

    getCommandName(): string {
        return 'GtthChangeLinkNodesDataCommand';
    }

    create(): this {
        return this;
    }

    addLinkToCommand(link: LinkFeature): void {
        if (!this.originalLinksMap.has(link.getId())) {
            const linkNodesData = {
                assignedName0: link.getAssignedName0(),
                assignedName1: link.getAssignedName1(),
                ip0: link.getNode0IPAddress(),
                ip1: link.getNode1IPAddress()
            };
            this.originalLinksMap.set(link.getId(), linkNodesData);
        }
    }

    getIsLinkModified(linkId): boolean {
        return this.modifiedLinksMap.has(linkId);
    }

    setNewAssignedNamesToLink(linkObj: LinkFeature, newAssignedName0: string, newAssignedName1: string): void {
        const linkId = linkObj.getId();
        const originalLinkData = this.originalLinksMap.get(linkId);
        let modifiedLinkData;

        if (this.modifiedLinksMap.has(linkId)) {
            modifiedLinkData = this.modifiedLinksMap.get(linkId);
        } else {
            modifiedLinkData = _cloneDeep(originalLinkData);
        }

        if (newAssignedName0) {
            linkObj.setAssignedName0(newAssignedName0);
            modifiedLinkData.assignedName0 = newAssignedName0;
        }
        if (newAssignedName1) {
            modifiedLinkData.assignedName1 = newAssignedName1;
            linkObj.setAssignedName1(newAssignedName1);
        }
        this.modifiedLinksMap.set(linkId, modifiedLinkData);
    }

    setNewIpsToLink(linkObj: LinkFeature, newIp0: string, newIp1: string): void {
        const linkId = linkObj.getId();
        const originalLinkData = this.originalLinksMap.get(linkId);
        let modifiedLinkData;

        if (this.modifiedLinksMap.has(linkId)) {
            modifiedLinkData = this.modifiedLinksMap.get(linkId);
        } else {
            modifiedLinkData = _cloneDeep(originalLinkData);
        }

        if (newIp0) {
            modifiedLinkData.ip0 = newIp0;
            linkObj.setNode0IPAddress(newIp0);
        }
        if (newIp1) {
            modifiedLinkData.ip1 = newIp1;
            linkObj.setNode1IPAddress(newIp1);
        }
        this.modifiedLinksMap.set(linkId, modifiedLinkData);
    }

    doCommand(): void {
        if (this.modifiedLinksMap?.size) {
            const activity = this.createActivityDTO(false);
            activity.links = [];

            for (const linkId of this.modifiedLinksMap.keys()) {
                const modifiedLinkData = this.modifiedLinksMap.get(linkId);
                const link = this.historyService.logicalTopology.getLink(linkId);

                if (link) {
                    link.setAssignedName0(modifiedLinkData.assignedName0);
                    link.setAssignedName1(modifiedLinkData.assignedName1);
                    link.setNode0IPAddress(modifiedLinkData.ip0);
                    link.setNode1IPAddress(modifiedLinkData.ip1);
                    activity.links.push(link.toGeoJson());
                }
            }
            if (activity.links?.length) {
                this.sendActivity(activity);
            }
        } else if (this.commandResolve) {
            this.commandResolve(false);
        }
    }

    undoCommand(): void {
        if (this.originalLinksMap?.size) {
            const activity = this.createActivityDTO(false);

            for (const linkId of this.originalLinksMap.keys()) {
                const originalLinkData = this.originalLinksMap.get(linkId);
                const link = this.historyService.logicalTopology.getLink(linkId);
                const linkChanged =
                    link.getAssignedName0() !== originalLinkData.assignedName0 ||
                    link.getAssignedName1() !== originalLinkData.assignedName1 ||
                    link.getNode0IPAddress() !== originalLinkData.ip0 ||
                    link.getNode1IPAddress() !== originalLinkData.ip1;
                if (link && linkChanged) {
                    link.setAssignedName0(originalLinkData.assignedName0);
                    link.setAssignedName1(originalLinkData.assignedName1);
                    link.setNode0IPAddress(originalLinkData.ip0);
                    link.setNode1IPAddress(originalLinkData.ip1);
                    activity.links.push(link.toGeoJson());
                }
            }
            if (activity.links?.length) {
                this.sendActivity(activity);
            }
        }
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            this.revertLinksConfiguration(activityDto, this.originalLinksMap);
        } else {
            this.applyLinksConfiguration(activityDto);
        }
    }

    undoCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            this.revertLinksConfiguration(activityDto, this.modifiedLinksMap);
        } else {
            this.applyLinksConfiguration(activityDto);
        }
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    private applyLinksConfiguration(activityDto: ActivityDTO): void {
        activityDto.links.forEach((activityDtoLink: GtthLinkFeatureDtoModel) => {
            const link = this.historyService.logicalTopology.getLink(activityDtoLink.getId());
            link.parseGeoJson(activityDtoLink);
            this.historyService.logicalTopology.updateLinkSector(link);
            this.historyService.bomReportService.triggerLinkState(link.getFromStation().getName(), link.getToStation().getName());
        });
        this.historyService.bomReportService.saveBom().then(() => {
            if (this.commandResolve) {
                this.commandResolve(activityDto.successful);
            }
        });
    }

    private revertLinksConfiguration(activityDto: ActivityDTO, linksMap: Map<number, LinkNodesData>): void {
        if (linksMap?.size) {
            linksMap.forEach((value: LinkNodesData, linkId: number) => {
                const link = this.historyService.logicalTopology.getLink(linkId);
                link.setAssignedName0(value.assignedName0);
                link.setAssignedName1(value.assignedName1);
                link.setNode0IPAddress(value.ip0);
                link.setNode1IPAddress(value.ip1);
            });
        }

        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }
}
